import { defaultAction } from "./defaultAction";
import {
  DISPLAY_MAIN_PAYMENT_ERROR,
  RESET_SELL,
  OPEN_ERROR_BOX,
  HIDE_LOADING,
  SHOW_LOADING,
  SHOW_ORDER_DECLINED,
  SELL_ORDER_PROCESS_RUNNING,
  SET_ORDER_ID,
  SHOW_TWILIO_POPUP
} from "@constants/actionTypes";
import { sendBugsnagError } from "@utils/Bugsnag";
import { getFormValues } from "redux-form";
import { trimString, removeEmpty } from "@utils";
import { errorResponseMessage } from "./errorResponseMessage";
import { signal } from "@modules/Signal/action";
import { isSchemaError } from "./isSchemaError";
import { openModal as openVerificationModal } from "@modules/OrderVerification/action";
import { fireOneOff } from "@modules/Emission/action";
import { getOrderSell } from "./sellOrderInfo";
import { getCardknoxData } from "@actions/getCardKnoxToken";

const withFlow = fn => {
  if (typeof fn !== "function") throw new Error("Fn should be a function");

  return async (dispatch, getState, context) => {
    try {
      dispatch(signal("flow-submit"));
      const result = await fn(dispatch, getState, context);
      return result;
    } catch (error) {
      sendBugsnagError(error, "actions/withFlow");
      return error;
    } finally {
      dispatch(signal("flow-end"));
    }
  };
};

export const createOrder = (order, history) =>
  withFlow(async (dispatch, getState, api) => {
    dispatch(defaultAction(SELL_ORDER_PROCESS_RUNNING, true));
    let orderInfo;
    let ecodeID, ecodeOrderID, physicalID, physicalOrderID;
    try {
      orderInfo = await api.post("v3/orders", order);
      if (
        orderInfo.data &&
        orderInfo.data.orders &&
        orderInfo.data.orders.ecode
      ) {
        ecodeID = orderInfo.data.orders.ecode.id;
        ecodeOrderID = orderInfo.data.orders.ecode.orderId;
      }

      if (
        orderInfo.data &&
        orderInfo.data.orders &&
        orderInfo.data.orders.physical
      ) {
        physicalID = orderInfo.data.orders.physical.id;
        physicalOrderID = orderInfo.data.orders.physical.orderId;
      }

      if ((ecodeID && physicalID) || (!ecodeID && !physicalID)) {
        dispatch(
          defaultAction(
            OPEN_ERROR_BOX,
            "Internal Error. Processing order failed to generate a single OrderID. Order cannot be processed.  Please contact us for assistance."
          )
        );
        dispatch(defaultAction(HIDE_LOADING));
        dispatch(defaultAction(SELL_ORDER_PROCESS_RUNNING, false));
        return;
      }
    } catch (error) {
      dispatch(defaultAction(SELL_ORDER_PROCESS_RUNNING, false));
      sendBugsnagError(error, "actions/createOrder/order");
      let errorMessage =
        errorResponseMessage(error) ||
        "We are unable to complete your order please check your information or refresh your page.";

      if (errorMessage.toLowerCase().includes("risk")) {
        history.push("/");
        dispatch(defaultAction(SHOW_ORDER_DECLINED, true));
        dispatch(defaultAction(RESET_SELL));
      } else if (isSchemaError(errorMessage)) {
        errorMessage =
          "We are unable to complete your order please check your information or refresh your page.";
      }

      dispatch(defaultAction(DISPLAY_MAIN_PAYMENT_ERROR, errorMessage));
      window.scrollTo(0, 0);
      dispatch(defaultAction(HIDE_LOADING));
      return;
    }

    let theID = ecodeID ? ecodeID : physicalID;
    let theOrderID = ecodeID ? ecodeOrderID : physicalOrderID;
    dispatch(defaultAction(SET_ORDER_ID, { id: theID, orderId: theOrderID }));
    try {
      await dispatch(getOrderSell(theID));
    } catch (error) {
      dispatch(defaultAction(SELL_ORDER_PROCESS_RUNNING, false));
      sendBugsnagError(error, "actions/createOrder/getOrder");
      let errorMessage =
        errorResponseMessage(error) ||
        "We are unable to complete your order please check your information or refresh your page.";
      if (isSchemaError(errorMessage)) {
        errorMessage =
          "We are unable to complete your order please check your information or refresh your page.";
      }

      dispatch(defaultAction(DISPLAY_MAIN_PAYMENT_ERROR, errorMessage));
      window.scrollTo(0, 0);
      dispatch(defaultAction(HIDE_LOADING));
      return;
    }

    if (orderInfo.data.orders.twilioRequired) {
      dispatch(defaultAction(SHOW_TWILIO_POPUP, true));
      dispatch(openVerificationModal());
      dispatch(
        fireOneOff("PV_INITIATED", {
          SIDE: "sell"
        })
      );
    } else {
      history.push("/order-complete/");
      dispatch(defaultAction(HIDE_LOADING));
      dispatch(defaultAction(SELL_ORDER_PROCESS_RUNNING, false));
    }
  });

export const completeSellOrder =
  (history, formName) => async (dispatch, getState) => {
    try {
      dispatch(defaultAction(SHOW_LOADING, "complete-sell"));
      const state = getState();
      const { cartSell } = state;
      let orderBase = {
        autoSplit: false,
        cartId: cartSell.cartId
      };

      const formValues = getFormValues(formName)(state);
      let billingDetails = {};
      let shippingDetails = {};
      if (formValues.shippingAddress) {
        if (formValues.shippingAddress.sameAsResidence) {
          shippingDetails = {
            firstname: trimString(formValues.primaryResidence.firstname),
            lastname: trimString(formValues.primaryResidence.lastname),
            street: trimString(formValues.primaryResidence.street),
            street2: trimString(formValues.primaryResidence.street2),
            city: trimString(formValues.primaryResidence.city),
            state: trimString(formValues.primaryResidence.state),
            postcode: trimString(formValues.primaryResidence.zip)
          };
        } else {
          shippingDetails = {
            firstname: trimString(formValues.shippingAddress.firstname),
            lastname: trimString(formValues.shippingAddress.lastname),
            street: trimString(formValues.shippingAddress.street),
            street2: trimString(formValues.shippingAddress.street2),
            city: trimString(formValues.shippingAddress.city),
            state: trimString(formValues.shippingAddress.state),
            postcode: trimString(formValues.shippingAddress.zip)
          };
        }
      }

      if (formValues.verification.sameAsShipping) {
        if (formValues.shippingAddress.sameAsResidence) {
          billingDetails = {
            firstname: trimString(formValues.primaryResidence.firstname),
            lastname: trimString(formValues.primaryResidence.lastname),
            street: trimString(formValues.primaryResidence.street),
            street2: trimString(formValues.primaryResidence.street2),
            city: trimString(formValues.primaryResidence.city),
            state: trimString(formValues.primaryResidence.state),
            postcode: trimString(formValues.primaryResidence.zip)
          };
        } else {
          billingDetails = {
            firstname: trimString(formValues.shippingAddress.firstname),
            lastname: trimString(formValues.shippingAddress.lastname),
            street: trimString(formValues.shippingAddress.street),
            street2: trimString(formValues.shippingAddress.street2),
            city: trimString(formValues.shippingAddress.city),
            state: trimString(formValues.shippingAddress.state),
            postcode: trimString(formValues.shippingAddress.zip)
          };
        }
      } else if (formValues.verification.sameAsPrimary) {
        billingDetails = {
          firstname: trimString(formValues.primaryResidence.firstname),
          lastname: trimString(formValues.primaryResidence.lastname),
          street: trimString(formValues.primaryResidence.street),
          street2: trimString(formValues.primaryResidence.street2),
          city: trimString(formValues.primaryResidence.city),
          state: trimString(formValues.primaryResidence.state),
          postcode: trimString(formValues.primaryResidence.zip)
        };
      } else {
        billingDetails = {
          firstname: trimString(formValues.verification.firstname),
          lastname: trimString(formValues.verification.lastname),
          street: trimString(formValues.verification.street),
          street2: trimString(formValues.verification.street2),
          city: trimString(formValues.verification.city),
          state: trimString(formValues.verification.state),
          postcode: trimString(formValues.verification.zip)
        };
      }

      removeEmpty(billingDetails);
      removeEmpty(shippingDetails);
      let monthAndYear =
        formValues.verification.creditCardInformation.ExpirationDate.replace(
          /\s+/g,
          ""
        ).split("/");

      const { cardNumToken, cvvNumToken } = await dispatch(getCardknoxData());

      let order = Object.assign(orderBase, {
        paymentMethod: cartSell.payoutMethod,
        creditCardDetails: {
          save: false,
          numberToken: cardNumToken,
          cvvToken: cvvNumToken,
          expMonth: trimString(monthAndYear[0]),
          expYear: trimString(monthAndYear[1])
        },
        billingDetails: billingDetails
      });

      if (cartSell.payoutMethod === "MAILCHECK") {
        order.shippingDetails = shippingDetails;
      } else if (cartSell.payoutMethod === "TRADEOPTION") {
        order.paymentId = cartSell.payoutMethodTradeID;
      } else if (cartSell.payoutMethod === "ACH") {
        order.paymentDetails = {
          id: formValues.enterACH.achRow.paymentDetails.id
        };
      }
      dispatch(createOrder(order, history));
    } catch (error) {
      dispatch(defaultAction(DISPLAY_MAIN_PAYMENT_ERROR, error.message));
      window.scrollTo(0, 0);
      dispatch(defaultAction(HIDE_LOADING));
      sendBugsnagError(error, "actions/completeSellOrder");
    }
  };
